import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getWearableSummary, formatSleepDuration, type WearableSummary } from '../api/wearable';

interface DashboardStats {
  documents: { total: number; parsed: number };
  parameters: { total: number; unique: number; outOfRange: number };
  conversations: { total: number };
  screenings: { total: number; pending: number };
  goals: { total: number; active: number };
  medications: { total: number; active: number };
}

export function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [wearableSummary, setWearableSummary] = useState<WearableSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchWearableSummary();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/v1/dashboard/stats', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWearableSummary = async () => {
    try {
      const data = await getWearableSummary(7);
      setWearableSummary(data.summary);
    } catch (error) {
      console.error('Failed to fetch wearable summary:', error);
    }
  };

  const statCards = [
    {
      title: 'Documenti',
      value: stats?.documents.total ?? 0,
      subtitle: 'Referti caricati',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      link: '/documents',
      linkText: 'Gestisci documenti',
    },
    {
      title: 'Parametri Lab',
      value: stats?.parameters.total ?? 0,
      subtitle: 'Valori estratti',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      link: '/parameters',
      linkText: 'Visualizza trend',
    },
    {
      title: 'Conversazioni',
      value: stats?.conversations.total ?? 0,
      subtitle: 'Con gli assistenti AI',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      link: '/chat',
      linkText: 'Vai alla chat',
    },
    {
      title: 'Screening',
      value: stats?.screenings.pending ?? 0,
      subtitle: 'Da programmare',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      link: null,
      linkText: null,
    },
  ];

  const agents = [
    {
      id: 'coordinator',
      name: 'Coordinatore',
      icon: '🧠',
      color: 'bg-gradient-to-br from-blue-500 to-blue-600',
      description: 'Analisi generale e coordinamento',
    },
    {
      id: 'nutritionist',
      name: 'Nutrizionista',
      icon: '🍎',
      color: 'bg-gradient-to-br from-green-500 to-green-600',
      description: 'Dieta e alimentazione',
    },
    {
      id: 'trainer',
      name: 'Trainer',
      icon: '💪',
      color: 'bg-gradient-to-br from-orange-500 to-orange-600',
      description: 'Fitness e allenamento',
    },
    {
      id: 'longevity',
      name: 'Longevity',
      icon: '⏳',
      color: 'bg-gradient-to-br from-teal-500 to-teal-600',
      description: 'Ottimizzazione salute',
    },
  ];

  return (
    <Layout title="Dashboard" showBackToDashboard={false}>
      <div className="max-w-7xl mx-auto">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {statCards.map((card) => (
            <div
              key={card.title}
              onClick={() => card.link && navigate(card.link)}
              className={`${card.bgColor} rounded-2xl shadow-sm p-6 lg:p-8 ${
                card.link ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
              }`}
            >
              <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-3">
                {card.title}
              </h3>
              <p className={`text-4xl lg:text-5xl font-bold ${card.color} mb-2`}>
                {loading ? '-' : card.value}
              </p>
              <p className="text-base lg:text-lg text-gray-600">{card.subtitle}</p>
              {card.linkText && (
                <p className={`text-sm lg:text-base ${card.color} mt-4 font-medium`}>
                  {card.linkText} →
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Additional Stats */}
        {stats && (stats.parameters.outOfRange > 0 || stats.goals.active > 0 || stats.medications.active > 0) && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
            {stats.parameters.outOfRange > 0 && (
              <div className="bg-red-50 rounded-xl p-5 lg:p-6 border border-red-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl lg:text-3xl font-bold text-red-600">{stats.parameters.outOfRange}</p>
                    <p className="text-sm lg:text-base text-red-700">Parametri fuori range</p>
                  </div>
                </div>
              </div>
            )}
            {stats.goals.active > 0 && (
              <div className="bg-indigo-50 rounded-xl p-5 lg:p-6 border border-indigo-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl lg:text-3xl font-bold text-indigo-600">{stats.goals.active}</p>
                    <p className="text-sm lg:text-base text-indigo-700">Obiettivi attivi</p>
                  </div>
                </div>
              </div>
            )}
            {stats.medications.active > 0 && (
              <div className="bg-cyan-50 rounded-xl p-5 lg:p-6 border border-cyan-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl lg:text-3xl font-bold text-cyan-600">{stats.medications.active}</p>
                    <p className="text-sm lg:text-base text-cyan-700">Farmaci attivi</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Wearable Widget */}
        {wearableSummary && (wearableSummary.steps.average > 0 || wearableSummary.sleep.averageMinutes > 0 || wearableSummary.workouts.total > 0) && (
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Attività Recente</h2>
              <button
                onClick={() => navigate('/wearable')}
                className="text-blue-600 hover:text-blue-700 text-sm lg:text-base font-semibold"
              >
                Vedi tutto →
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
              <div
                onClick={() => navigate('/wearable')}
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 lg:p-6 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600">Passi (media 7gg)</span>
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-blue-600">
                  {wearableSummary.steps.average.toLocaleString('it-IT')}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Oggi: {wearableSummary.steps.latest.toLocaleString('it-IT')}
                </p>
              </div>

              <div
                onClick={() => navigate('/wearable')}
                className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 lg:p-6 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600">Sonno (media 7gg)</span>
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-purple-600">
                  {formatSleepDuration(wearableSummary.sleep.averageMinutes)}
                </p>
                {wearableSummary.sleep.avgHRV && (
                  <p className="text-sm text-gray-500 mt-1">
                    HRV: {wearableSummary.sleep.avgHRV} ms
                  </p>
                )}
              </div>

              <div
                onClick={() => navigate('/wearable')}
                className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-5 lg:p-6 cursor-pointer hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-600">Allenamenti (7gg)</span>
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-orange-600">
                  {wearableSummary.workouts.total}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {wearableSummary.workouts.totalMinutes} minuti totali
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AI Assistants section */}
        <div className="mt-12 lg:mt-16">
          <div className="flex justify-between items-center mb-6 lg:mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Assistenti AI
            </h2>
            <button
              onClick={() => navigate('/chat')}
              className="text-blue-600 hover:text-blue-700 text-base lg:text-lg font-semibold"
            >
              Vai alla Chat →
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {agents.map((agent) => (
              <button
                key={agent.id}
                onClick={() => navigate('/chat')}
                className="bg-white rounded-2xl shadow-sm p-6 lg:p-8 text-left hover:shadow-lg transition-all transform hover:-translate-y-1"
              >
                <div
                  className={`w-14 h-14 lg:w-16 lg:h-16 ${agent.color} rounded-2xl flex items-center justify-center text-white text-2xl lg:text-3xl mb-4 shadow-md`}
                >
                  {agent.icon}
                </div>
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">{agent.name}</h3>
                <p className="text-base lg:text-lg text-gray-600">{agent.description}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
